import { useEffect, useMemo, useRef, useState, type PointerEvent as RPointerEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExerciseTile } from './ExerciseTile';
import { useLightbox } from './Lightbox';
import { SLOTS, CREDIT, feedRank, type Slot } from '../data/film';
import { liveChallenges } from '../data/exercises';
import { dealFromFilm } from '../lib/film-deal';
import { optionsFor, logline, lineInput, pieceKey, filmKicker, SLOT_HUE, SLOT_NOUN, type FilmPiece } from '../lib/pieces';
import { sayFilmLine, type LinePart } from '../lib/crew';
import { track } from '../analytics';
import type { Challenge } from '../data/types';

/**
 * The film table (K33 S-03, owner 2026-09-21: "less like UI, more like a fun
 * interactive game"; "a compact and beautiful summary on the home page, but
 * the interaction more luxurious and full screen").
 *
 * `compact` is the summary: five small cards and the film's one line, the
 * whole thing a button that opens the table.
 *
 * `table` is the room: a poster of the film as it stands (your pictures, your
 * line), five card slots on the wood, and your pieces fanned in a hand below.
 * Tap a slot and the hand shows only pieces of that kind; tap a piece and it
 * flies up into its slot; or pull it up and drop it there. Everything is free
 * and undoable; only Roll it costs.
 */
export interface FilmTableProps {
  pieces: FilmPiece[];
  current: Partial<Record<Slot, FilmPiece>>;
  onPick: (slot: Slot, piece: FilmPiece) => void;
  /** the slot the exercises are aimed at: its empty slot breathes */
  next?: Slot | null;
  kicker?: string;
  mode: 'compact' | 'table';
  /** compact: opens the table */
  onOpen?: () => void;
  /** compact: the slot a piece just flew into (it drops in and glows), and each slot's box for the piece that flies */
  landing?: Slot | null;
  slotRef?: (s: Slot, el: HTMLDivElement | null) => void;
  /** table: the rolled film, once there is one — it takes the poster's place */
  poster?: { url?: string; title: string; videoUrl?: string } | null;
  /** table: the crew's poster of a row (current when its key is this row's) */
  drawn?: { key: string; url: string } | null;
  /** table: ask the crew to draw the row as it stands; resolves when it is on the table */
  onDraw?: () => Promise<void>;
  drawing?: boolean;
}

export function FilmTable(props: FilmTableProps) {
  return props.mode === 'compact' ? <CompactRow {...props} /> : <Table {...props} />;
}

/**
 * The summary on Home (owner, 2026-09-22: "too busy… think in the greater
 * context of a section on a mobile page"): one line of words, five cards, a
 * chevron. No bands on the cards, no logline; those live on the table.
 */
function CompactRow({ current, next, kicker, onOpen, landing, slotRef }: FilmTableProps) {
  return (
    <button type="button" className={`film-row ${onOpen ? '' : 'still'}`} onClick={onOpen} aria-label={onOpen ? 'Open your film table' : 'Your first film'}>
      <div className="film-row-head">
        <span className="table-kicker">{kicker ?? filmKicker(current)}</span>
        {onOpen && <span className="film-row-open" aria-hidden>›</span>}
      </div>
      <div className="film-row-cards">
        {SLOTS.map((s) => (
          <div key={s} className={`mini ${current[s] ? 'filled' : 'empty'} ${next === s && !current[s] ? 'breathe' : ''} ${landing === s ? 'landing' : ''}`} ref={(el) => slotRef?.(s, el)}>
            <PieceCard key={current[s]?.id ?? 'empty'} piece={current[s]} slot={s} size="mini" />
            <span className="mini-label">{CREDIT[s].label}</span>
          </div>
        ))}
      </div>
    </button>
  );
}

function Table({ pieces, current, onPick, next, kicker, poster, drawn, onDraw, drawing }: FilmTableProps) {
  const nav = useNavigate();
  const { open, lightbox } = useLightbox();
  const [sel, setSel] = useState<Slot | null>(null);
  const [landing, setLanding] = useState<Slot | null>(null);
  const [hot, setHot] = useState<Slot | null>(null);
  const [spinning, setSpinning] = useState(false);
  const [flight, setFlight] = useState<{ piece: FilmPiece; from: DOMRect; to: DOMRect; how: 'tap' } | null>(null);
  const slotEls = useRef<Partial<Record<Slot, HTMLDivElement | null>>>({});
  const flightEl = useRef<HTMLDivElement>(null);
  const landTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const options = useMemo(() => Object.fromEntries(SLOTS.map((s) => [s, optionsFor(pieces, s)])) as Record<Slot, FilmPiece[]>, [pieces]);
  const filled = SLOTS.filter((s) => current[s]).length;
  const canSpin = SLOTS.some((s) => options[s].length > 1);
  useEffect(() => () => { if (landTimer.current) clearTimeout(landTimer.current); }, []);

  const land = (slot: Slot, piece: FilmPiece, how: 'spin_rest' | 'tap' | 'drag') => {
    onPick(slot, piece);
    setLanding(slot);
    track('film_pick', { slot, how, source: piece.source });
    try { navigator.vibrate?.(8); } catch { /* not every phone */ }
    if (landTimer.current) clearTimeout(landTimer.current);
    landTimer.current = setTimeout(() => setLanding(null), 900);
  };
  const slotRect = (s: Slot) => slotEls.current[s]?.querySelector('.fslot-window')?.getBoundingClientRect() ?? null;
  const show = (piece: FilmPiece) => { if (piece.img) open(piece.img, `${piece.text} · ${piece.title}`); };

  /** A tapped piece flies from the hand up into its slot, then lands. */
  const seat = (piece: FilmPiece, how: 'tap' | 'drag', from?: DOMRect) => {
    if (current[piece.slot]?.id === piece.id) { show(piece); return; }
    const to = slotRect(piece.slot);
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (how === 'tap' && from && to && !still && 'animate' in HTMLElement.prototype) { setFlight({ piece, from, to, how }); return; }
    land(piece.slot, piece, how);
  };
  useEffect(() => {
    if (!flight) return;
    const el = flightEl.current;
    const finish = () => { land(flight.piece.slot, flight.piece, flight.how); setFlight(null); };
    if (!el) { finish(); return; }
    const { from, to } = flight;
    const midX = (from.left + to.left) / 2; const midY = Math.min(from.top, to.top) - 36;
    const a = el.animate(
      [{ transform: `translate(${from.left}px, ${from.top}px) rotate(-3deg)` }, { transform: `translate(${midX}px, ${midY}px) rotate(5deg) scale(1.04)`, offset: 0.45 }, { transform: `translate(${to.left}px, ${to.top}px) scale(${to.width / from.width}, ${to.height / from.height}) rotate(0deg)` }],
      { duration: 620, easing: 'cubic-bezier(.5,0,.2,1)', fill: 'forwards' },
    );
    a.finished.then(finish, finish);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flight]);

  const spinRest = () => {
    const targets = SLOTS.filter((s) => options[s].length > 1);
    if (!targets.length) return;
    setSpinning(true);
    track('film_spin_rest', { slots: targets.length });
    targets.forEach((s, k) => setTimeout(() => {
      const others = options[s].filter((o) => o.id !== current[s]?.id);
      land(s, others[Math.floor(Math.random() * others.length)], 'spin_rest');
    }, 130 * k));
    setTimeout(() => setSpinning(false), 130 * targets.length + 500);
  };

  const has = useMemo(() => Object.fromEntries(SLOTS.filter((s) => current[s]).map((s) => [s, current[s]!.text])) as Partial<Record<Slot, string>>, [current]);
  const finders = useMemo(() => sel ? liveChallenges().filter((c) => c.purpose !== 'open' && !c.input.private && feedRank(c, sel) !== null).sort((a, b) => feedRank(a, sel)! - feedRank(b, sel)! || a.minutes - b.minutes).slice(0, 4) : [], [sel]);
  const goFind = (c: Challenge) => {
    const { deal, fromFilm } = dealFromFilm(c, has);
    track('home_pick', { challengeId: c.id, how: 'slot', gap: sel ?? undefined, fromFilm: fromFilm.length });
    nav(`/do/${c.id}`, { state: { door: 'front', deal, fromFilm } });
  };
  const inHand = sel ? options[sel] : pieces;

  return (
    <>
      <section className="tabletop" aria-label="Your first film">
        <div className="table-head">
          <span className="table-kicker">{kicker ?? `Your first film · ${filled} of ${SLOTS.length}`}</span>
          {canSpin && <button type="button" className="spin-btn" onClick={spinRest} disabled={spinning}>{spinning ? 'Spinning' : 'Spin the rest'}</button>}
        </div>
        <Poster current={current} rolled={poster} drawn={drawn} onDraw={onDraw} drawing={drawing} />
        <div className="fslots">
          {SLOTS.map((s) => {
            const p = current[s];
            const n = options[s].length;
            return (
              <div key={s} className={`fslot ${p ? 'filled' : 'empty'} ${sel === s ? 'sel' : ''} ${hot === s ? 'hot' : ''} ${landing === s ? 'landing' : ''} ${next === s && !p ? 'breathe' : ''}`} ref={(el) => { slotEls.current[s] = el; }}>
                <span className="fslot-label">{CREDIT[s].label}</span>
                <button type="button" className="fslot-window" onClick={() => { const on = sel === s ? null : s; setSel(on); track('film_slot_tap', { slot: s, on: !!on, options: n }); }} aria-pressed={sel === s}
                  aria-label={p ? `${CREDIT[s].label}: ${p.text}. ${n > 1 ? `${n} of your pieces fit here` : 'the only piece of this kind'}` : `${CREDIT[s].label}: nothing yet. Find ${SLOT_NOUN[s]}`}>
                  <div key={p?.id ?? 'empty'} className="fslot-card"><PieceCard piece={p} slot={s} size="slot" /></div>
                </button>
                <span className={`fslot-count ${p ? '' : 'find'}`}>{p ? (n > 1 ? `${n} fit` : ' ') : 'find one'}</span>
              </div>
            );
          })}
        </div>
      </section>

      <div className="hand" aria-label="Your pieces">
        <div className="row between" style={{ alignItems: 'center' }}>
          <span className="label">{sel ? `${CREDIT[sel].label} · ${inHand.length} of your pieces` : `Your pieces · ${pieces.length}`}</span>
          {sel ? <button type="button" className="chip on" onClick={() => setSel(null)}>Show all</button> : <span className="hint">Tap one to seat it, or pull it up</span>}
        </div>
        {inHand.length > 0
          ? <Hand pieces={inHand} current={current} slotRect={slotRect} onHover={setHot} onPlace={seat} />
          : <p className="why">{sel ? `Nothing of yours is ${SLOT_NOUN[sel]} yet.` : 'Every exercise you finish puts a piece here.'}</p>}
        {/* for every kind, not only an empty one (owner, 2026-09-22): if you have nothing you like yet, there is an activity to go get it */}
        {sel && finders.length > 0 && (
          <div className="find-inline">
            <span className="label">{inHand.length ? `Make another ${SLOT_NOUN[sel]}` : `Make ${SLOT_NOUN[sel]}`}</span>
            <p className="why">{inHand.length ? `Not the one yet? These make ${SLOT_NOUN[sel]}; whatever you write lands here.` : `These make ${SLOT_NOUN[sel]}; whatever you write lands here.`}</p>
            <div className="tile-strip">{finders.map((c) => <ExerciseTile key={c.id} c={c} small onPick={goFind} />)}</div>
          </div>
        )}
      </div>

      {flight && (
        <div ref={flightEl} className="drag-ghost flight" style={{ width: flight.from.width, height: flight.from.height, transform: `translate(${flight.from.left}px, ${flight.from.top}px)` }} aria-hidden>
          <PieceCard piece={flight.piece} slot={flight.piece.slot} size="hand" />
        </div>
      )}
      {lightbox}
    </>
  );
}

/** The film as a poster (K33 S-03's live poster): your pictures, your line. Until the roll it is a preview; after, the rolled film's own. */
function Poster({ current, rolled, drawn, onDraw, drawing }: { current: Partial<Record<Slot, FilmPiece>>; rolled?: { url?: string; title: string; videoUrl?: string } | null; drawn?: { key: string; url: string } | null; onDraw?: () => Promise<void>; drawing?: boolean }) {
  if (rolled) {
    return (
      <div className="poster rolled">
        <div className="poster-art n1">
          {rolled.videoUrl ? <video className="poster-video" src={rolled.videoUrl} poster={rolled.url} controls playsInline preload="metadata" /> : <img src={rolled.url} alt={rolled.title} />}
        </div>
        <b className="poster-title">{rolled.title}</b>
        <span className="poster-tag">Rolled by your crew</span>
      </div>
    );
  }
  const key = pieceKey(current);
  const seated = SLOTS.filter((s) => current[s]).length;
  const fresh = !!drawn && drawn.key === key;
  const imgs = ([current.someone, current.somewhere, current['a look'], current['a want']].filter((p) => p?.img) as FilmPiece[]).slice(0, 3);
  return (
    <div className={`poster ${fresh ? 'drawn' : ''} ${imgs.length || drawn ? '' : 'blank'}`}>
      <div className={`poster-art ${fresh || drawn ? 'n1' : `n${imgs.length}`} ${drawn && !fresh ? 'stale' : ''}`}>
        {drawn
          ? <img src={drawn.url} alt="The poster your crew drew of this row" />
          : imgs.map((p) => p.sheet ? <div key={p.id} className="mini-sheet"><img src={p.img} alt={p.text} /></div> : <img key={p.id} src={p.img} alt={p.text} />)}
        {!drawn && !imgs.length && <span className="poster-empty">Your pictures go here</span>}
      </div>
      <Logline current={current} className="poster-line" />
      {onDraw && !fresh && seated >= 2 && (
        <button type="button" className="btn small butter poster-draw" onClick={() => { track('film_poster_draw', { seated }); onDraw(); }} disabled={drawing}>
          {drawing ? 'Your crew are drawing it' : drawn ? 'Redraw for this row' : 'Draw the poster'}
        </button>
      )}
      {!onDraw || fresh ? null : seated < 2 && <span className="hint" style={{ padding: '0 4px' }}>Two pieces on the table and your crew can draw the poster.</span>}
      <span className="poster-tag">{fresh ? 'Drawn by your crew' : drawn ? 'From an earlier row' : 'Preview'}</span>
    </div>
  );
}

// Lines the crew have said this session, by the words on the table: a swap back to a row you had is instant.
const saidLines = new Map<string, LinePart[] | null>();
let lineCrewOff = false;
/** How long the table waits after a card lands before asking; a quick run of swaps asks once. */
const SAY_AFTER_MS = 350;

/**
 * The crew's sentence for the row, when they have said one. Until then (and
 * with the crew off, or fewer than two pieces) it is null and the table shows
 * the pieces plainly; `settling` is the moment in between.
 */
function useSaidLine(current: Partial<Record<Slot, FilmPiece>>): { parts: LinePart[] | null; settling: boolean } {
  const input = lineInput(current);
  const key = JSON.stringify(input);
  const worth = Object.keys(input).length >= 2 && !lineCrewOff;
  const [, heard] = useState(0);
  useEffect(() => {
    if (!worth || saidLines.has(key)) return;
    let live = true;
    const t = setTimeout(() => {
      sayFilmLine(input)
        .then((out) => { saidLines.set(key, out.parts); if (out.parts) track('film_line_said', { cached: out.cached }); })
        .catch((err: { code?: string }) => { if (err?.code === 'crew_off') lineCrewOff = true; saidLines.set(key, null); })
        .finally(() => { if (live) heard((n) => n + 1); });
    }, SAY_AFTER_MS);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- key is the input
  }, [key, worth]);
  return { parts: worth ? saidLines.get(key) ?? null : null, settling: worth && !saidLines.has(key) };
}

function Logline({ current, className }: { current: Partial<Record<Slot, FilmPiece>>; className: string }) {
  const { parts, settling } = useSaidLine(current);
  const first = current['a line'];
  if (parts) {
    return (
      <p className={`${className} said`}>
        {parts.map((p, i) => p.slot ? <b key={i} className="is">{p.text}</b> : <span key={i}>{p.text}</span>)}
        {first && <span> It opens: “<b className="is">{first.text}</b>”</span>}
      </p>
    );
  }
  return (
    <p className={`${className} plain ${settling ? 'settling' : ''}`}>
      {logline(current).map((b) => <span key={b.slot}>{b.before}<b className={b.filled ? 'is' : 'blank'}>{b.text}</b>{b.after}</span>)}
    </p>
  );
}

/** One piece as a card: a coloured band by kind, then its picture or your words. */
export function PieceCard({ piece, slot, size }: { piece?: FilmPiece; slot: Slot; size: 'mini' | 'slot' | 'hand' }) {
  return (
    <div className={`pcard ${SLOT_HUE[slot]} sz-${size} ${piece ? '' : 'empty'}`}>
      <span className="pcard-band">{CREDIT[slot].label}</span>
      <div className="pcard-face">
        {piece?.img
          ? (piece.sheet ? <div className="mini-sheet"><img src={piece.img} alt={piece.text} draggable={false} /></div> : <img src={piece.img} alt={piece.text} draggable={false} />)
          : piece ? <em>{piece.text}</em> : <KindGlyph slot={slot} />}
      </div>
    </div>
  );
}

const within = (r: DOMRect, x: number, y: number, pad: number) => x >= r.left - pad && x <= r.right + pad && y >= r.top - pad && y <= r.bottom + pad;

/**
 * Your pieces, fanned. A tap seats a piece (it flies up). A pull upward lifts
 * it off the hand to be dropped on its slot; the strip still scrolls sideways
 * under the thumb (touch-action pan-x hands us only the vertical moves).
 */
function Hand({ pieces, current, slotRect, onHover, onPlace }: {
  pieces: FilmPiece[]; current: Partial<Record<Slot, FilmPiece>>; slotRect: (s: Slot) => DOMRect | null; onHover: (s: Slot | null) => void; onPlace: (p: FilmPiece, how: 'tap' | 'drag', from?: DOMRect) => void;
}) {
  const start = useRef<{ x: number; y: number; piece: FilmPiece; id: number } | null>(null);
  const dragging = useRef(false);
  const justDragged = useRef(false);
  const [ghost, setGhost] = useState<{ piece: FilmPiece; x: number; y: number } | null>(null);

  const down = (e: RPointerEvent<HTMLButtonElement>, piece: FilmPiece) => { start.current = { x: e.clientX, y: e.clientY, piece, id: e.pointerId }; dragging.current = false; };
  const move = (e: RPointerEvent<HTMLButtonElement>) => {
    const s = start.current;
    if (!s || e.pointerId !== s.id) return;
    const dx = e.clientX - s.x; const dy = e.clientY - s.y;
    if (!dragging.current) {
      if (dy < -14 && Math.abs(dy) > Math.abs(dx)) { dragging.current = true; try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* fine */ } track('film_drag_start', { slot: s.piece.slot }); }
      else return;
    }
    setGhost({ piece: s.piece, x: e.clientX, y: e.clientY });
    const r = slotRect(s.piece.slot);
    onHover(r && within(r, e.clientX, e.clientY, 22) ? s.piece.slot : null);
  };
  const end = (e: RPointerEvent<HTMLButtonElement>, cancelled = false) => {
    const s = start.current;
    start.current = null;
    if (!s || !dragging.current) return;
    dragging.current = false;
    justDragged.current = true;
    setTimeout(() => { justDragged.current = false; }, 350);
    onHover(null); setGhost(null);
    const r = slotRect(s.piece.slot);
    if (!cancelled && r && within(r, e.clientX, e.clientY, 22)) onPlace(s.piece, 'drag');
    else track('film_drag_back', { slot: s.piece.slot });
  };

  return (
    <>
      <div className="hand-strip">
        {pieces.map((p) => {
          const inFilm = current[p.slot]?.id === p.id;
          return (
            <button key={p.id} type="button" className={`hcard ${inFilm ? 'in' : ''}`} aria-label={`${p.text}, ${SLOT_NOUN[p.slot]}${inFilm ? ', in the film' : ''}`}
              onPointerDown={(e) => down(e, p)} onPointerMove={move} onPointerUp={(e) => end(e)} onPointerCancel={(e) => end(e, true)}
              onClick={(e) => { if (justDragged.current) return; onPlace(p, 'tap', e.currentTarget.querySelector('.pcard')?.getBoundingClientRect()); }}>
              <PieceCard piece={p} slot={p.slot} size="hand" />
              <span className="hcard-cap">{p.title}</span>
            </button>
          );
        })}
      </div>
      {ghost && (
        <div className="drag-ghost" style={{ transform: `translate(${ghost.x - 48}px, ${ghost.y - 80}px) rotate(-5deg)` }} aria-hidden>
          <PieceCard piece={ghost.piece} slot={ghost.piece.slot} size="hand" />
        </div>
      )}
    </>
  );
}

/** The shape of a kind of piece, for an empty slot: you can see what would fit. */
export function KindGlyph({ slot }: { slot: Slot }) {
  const common = { width: 34, height: 34, viewBox: '0 0 34 34', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  switch (slot) {
    case 'someone': return <svg {...common}><circle cx="17" cy="11" r="6" /><path d="M6 30c0-6 5-10 11-10s11 4 11 10" /></svg>;
    case 'somewhere': return <svg {...common}><rect x="4" y="7" width="26" height="20" rx="4" /><circle cx="11" cy="14" r="2.5" /><path d="M5 25l8-8 6 6 4-4 6 6" /></svg>;
    case 'a want': return <svg {...common}><ellipse cx="18" cy="13" rx="11" ry="8" /><circle cx="9" cy="25" r="2.5" /><circle cx="5" cy="30" r="1.5" /></svg>;
    case 'a look': return <svg {...common}><g transform="rotate(-8 17 17)"><rect x="6" y="6" width="22" height="22" rx="4" /><path d="M8 14h18M8 20h18" /></g></svg>;
    default: return <svg {...common}><path d="M5 6h24a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H14l-6 5v-5H5a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3z" /><path d="M9 13h16M9 18h10" /></svg>;
  }
}
